/**
 * ForwardDeploySection — "forward-deployed" device-support section, placed directly
 * below SetupSection ("From setup to data in minutes"). It frames StudySync's
 * forward-deployed approach: we personalize setup to each study and meet participants
 * on whatever wearable they already own.
 *
 * TWO-COLUMN layout (stacks on mobile):
 *   LEFT  — a 3D device row (DeviceScene): Apple Watch swings in first, then Fitbit,
 *           then a faint ghosted third device hinting "your next device." Below it, a
 *           3-up row of series names aligned under the devices.
 *   RIGHT — the large headline + supporting copy + the "Tell us about your study" CTA.
 *
 * DeviceScene (three.js + r3f + drei) is `React.lazy`'d AND only mounted once the
 * canvas nears the viewport (IntersectionObserver), so its chunk + the ~1.8 MB of GLBs
 * load on demand, not on initial page load.
 *
 * The CTA opens the shared contact dialog (reusing the 'talk-to-us' source). Styling
 * matches SetupSection (filled brutalist badge → font-head headline with a Teal accent
 * → scroll-triggered framer-motion reveals). Tailwind Preflight is OFF, so every border
 * is declared `border-solid`. ZERO MUI.
 */
import * as React from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { useContactDialog } from './ContactDialogProvider';

// Heavy 3D scene — code-split into its own chunk, loaded on demand (see below).
const DeviceScene = React.lazy(() => import('./DeviceScene'));

/** Series names under the canvas — mirrors the 3D device order left → right. */
const DEVICE_LEGEND: ReadonlyArray<{ label: string; accent: string; ghost?: boolean }> = [
  { label: 'Apple Watch', accent: 'text-secondary' },
  { label: 'Fitbit', accent: 'text-primary' },
  { label: 'Your next device', accent: 'text-muted-foreground', ghost: true },
];

const ForwardDeploySection: React.FC = () => {
  const reduce = useReducedMotion();
  const { open } = useContactDialog();

  // Mount the 3D chunk only when the canvas area approaches the viewport.
  const sceneRef = React.useRef<HTMLDivElement>(null);
  const [inView, setInView] = React.useState(false);
  // Narrow viewport → tighter device spacing/size in the scene.
  const [compact, setCompact] = React.useState(false);

  React.useEffect(() => {
    const el = sceneRef.current;
    if (!el) return;
    if (typeof IntersectionObserver === 'undefined') {
      setInView(true);
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setInView(true);
          io.disconnect();
        }
      },
      { rootMargin: '200px 0px' },
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  React.useEffect(() => {
    if (typeof window.matchMedia !== 'function') return;
    const mq = window.matchMedia('(max-width: 768px)');
    const update = () => setCompact(mq.matches);
    update();
    mq.addEventListener('change', update);
    return () => mq.removeEventListener('change', update);
  }, []);

  /** Shared "fade + slide up" reveal, gated on reduced motion (mounts at rest). */
  const fadeUp = (delay = 0, duration = 0.5) =>
    reduce
      ? ({ initial: false as const } as const)
      : ({
          initial: { opacity: 0, y: 24 },
          whileInView: { opacity: 1, y: 0 },
          viewport: { once: true, amount: 0.3 },
          transition: { duration, ease: 'easeOut', delay },
        } as const);

  return (
    <section
      id="devices"
      className="bg-background py-20 md:py-28"
    >
      <div className="mx-auto max-w-[1536px] px-4 md:px-8">
        {/* Device column stays wider than the text column — wide enough that the 3-device
            row + their contact shadows don't clip at the sides. The wider max-width +
            tighter gap keep the device column just as roomy while giving the TEXT column
            ~100px more, so the headline's two phrases each fit on ONE line (2 lines total)
            instead of wrapping to four. */}
        {/* Mobile order: headline → 3D devices → "don't see it?" CTA. On lg the 3D
            column sits LEFT (spanning both text rows) with the headline top-right and
            the CTA bottom-right — same desktop layout, just split into 3 blocks so the
            devices can slot BETWEEN the headline and CTA when stacked on a phone. */}
        <div className="grid items-center gap-x-12 gap-y-8 lg:grid-cols-[1.25fr_1fr] lg:gap-y-6">
          {/* ===== A — eyebrow + headline. Mobile: top. lg: top-RIGHT. ===== */}
          <div className="text-center lg:col-start-2 lg:row-start-1 lg:self-end lg:text-left">
            <motion.span
              {...fadeUp(0)}
              className="mx-auto block w-fit border-2 border-solid border-black bg-green px-3 py-1 font-mono text-xs font-bold uppercase tracking-[0.2em] text-white lg:mx-0"
            >
              Forward-deployed
            </motion.span>

            <motion.h2
              {...fadeUp(0.08)}
              className="mt-6 font-head text-3xl uppercase leading-tight text-foreground md:text-4xl"
            >
              <span className="block lg:whitespace-nowrap">Built around your study,</span>
              <span className="block text-green lg:whitespace-nowrap">and your devices</span>
            </motion.h2>
          </div>

          {/* ===== B — 3D device row + series names. Mobile: MIDDLE. lg: LEFT, spans both rows. ===== */}
          <div className="lg:col-start-1 lg:row-span-2 lg:row-start-1">
            <div
              ref={sceneRef}
              className="relative h-[380px] w-full sm:h-[440px] lg:h-[520px]"
            >
              {/* Subtle brand glow behind the devices. */}
              <div
                aria-hidden
                className="pointer-events-none absolute inset-0 z-0 mx-auto my-auto h-[64%] w-[78%] rounded-full bg-secondary/15 blur-3xl"
              />
              <div className="absolute inset-0 z-10">
                {inView && (
                  <React.Suspense fallback={null}>
                    <DeviceScene reduce={!!reduce} compact={compact} />
                  </React.Suspense>
                )}
              </div>
            </div>

            {/* Series names — one per device, aligned in a 3-up row under the canvas. */}
            <div className="grid grid-cols-3 gap-2 sm:gap-3">
              {DEVICE_LEGEND.map((d, i) => (
                <motion.div
                  {...fadeUp(0.35 + i * 0.45, 0.85)}
                  key={d.label}
                  className="text-center"
                >
                  <span
                    className={[
                      'inline-block border-2 border-solid px-2 py-1 font-sans text-[10px] font-bold uppercase tracking-[0.14em] sm:text-xs',
                      d.ghost
                        ? 'border-dashed border-muted-foreground/40 text-muted-foreground/70'
                        : `border-black ${d.accent}`,
                    ].join(' ')}
                  >
                    {d.label}
                  </span>
                </motion.div>
              ))}
            </div>
          </div>

          {/* ===== C — "don't see it?" + CTA. Mobile: bottom. lg: bottom-RIGHT. ===== */}
          <div className="text-center lg:col-start-2 lg:row-start-2 lg:self-start lg:text-left">
            {/* Matches the muted sans subhead style used elsewhere (e.g. the StudiesMap
                "From California labs…" line): font-sans, not uppercase, muted color. */}
            <motion.p
              {...fadeUp(0.16)}
              className="font-sans text-base text-muted-foreground sm:text-lg"
            >
              Don’t see your device yet?
            </motion.p>

            <motion.div {...fadeUp(0.22)}>
              <button
                type="button"
                onClick={() => open('talk-to-us')}
                className="mt-4 inline-flex items-center border-2 border-solid border-black bg-green px-7 py-3 font-sans text-sm font-bold uppercase tracking-[0.15em] text-white shadow-[3px_3px_0_0_#000] transition hover:-translate-y-[1px] hover:brightness-95 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-green"
              >
                Tell us about your study
              </button>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ForwardDeploySection;
